/**
 * A gateway's birth message (design 2026-09-24 section 7.2), read defensively.
 *
 * `GET /v1/things/{id}` returns the birth exactly as the gateway sent it
 * (`capabilities`), so any field can be missing, null or the wrong shape: a
 * pre-v0.3 gateway sends no birth at all, and `networks` arrived later than the
 * rest. Everything the web shows about a gateway's firmware, drivers, serial
 * ports and networks goes through {@link readBirth}, which only ever returns
 * well-formed values.
 */

import type { GatewayCapabilities, GatewayNetwork, Thing } from "@/lib/api/types";

export interface GatewayBirth {
  /** Firmware version, e.g. "0.3.0". */
  fw: string | null;
  /** "linux / arm64". */
  host: string | null;
  /** Config schema versions the gateway accepts; `2` means multi-protocol config. */
  configVersions: number[];
  /** Driver ids the firmware includes, with their versions. */
  drivers: Array<{ id: string; v?: string }>;
  /** Serial ports the gateway can open (Modbus RTU picks from these). */
  serialPorts: string[];
  configHash: string | null;
  /**
   * The gateway's interfaces, or null when the birth has no `networks` (older
   * firmware): the Discover step then falls back to a free-text range.
   */
  networks: GatewayNetwork[] | null;
}

const EMPTY: GatewayBirth = {
  fw: null,
  host: null,
  configVersions: [],
  drivers: [],
  serialPorts: [],
  configHash: null,
  networks: null,
};

const str = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v.trim() : null);
const strings = (v: unknown): string[] =>
  Array.isArray(v) ? v.flatMap((x) => (typeof x === "string" && x.trim() !== "" ? [x.trim()] : [])) : [];

/** One `networks` entry, or null when it is not an object with an interface name. */
function readNetwork(v: unknown): GatewayNetwork | null {
  if (typeof v !== "object" || v === null) return null;
  const n = v as Record<string, unknown>;
  const iface = str(n.iface);
  if (!iface) return null;
  const kind = str(n.kind);
  return {
    iface,
    kind: kind === "ethernet" || kind === "wifi" ? kind : (kind ?? "other"),
    up: n.up === true,
    cidrs: strings(n.cidrs),
    gateway: str(n.gateway),
    default_route: n.default_route === true,
    ssid: str(n.ssid),
  };
}

/** The birth of a gateway Thing (or a raw `capabilities` object), always well-formed. */
export function readBirth(source: Pick<Thing, "capabilities"> | GatewayCapabilities | null | undefined): GatewayBirth {
  const caps =
    source && typeof source === "object" && "capabilities" in source
      ? (source as Pick<Thing, "capabilities">).capabilities
      : (source as GatewayCapabilities | null | undefined);
  if (!caps || typeof caps !== "object") return EMPTY;
  const host = caps.host && typeof caps.host === "object" ? caps.host : null;
  const hostText = [str(host?.os), str(host?.arch)].filter(Boolean).join(" / ");
  return {
    fw: str(caps.fw),
    host: hostText || null,
    configVersions: Array.isArray(caps.config_versions)
      ? caps.config_versions.filter((x): x is number => typeof x === "number")
      : [],
    drivers: Array.isArray(caps.drivers)
      ? caps.drivers.flatMap((d) => {
          if (typeof d !== "object" || d === null) return [];
          const id = str((d as { id?: unknown }).id);
          const v = str((d as { v?: unknown }).v);
          return id ? [v ? { id, v } : { id }] : [];
        })
      : [],
    serialPorts: strings(caps.serial_ports),
    configHash: str(caps.config_hash),
    networks: Array.isArray(caps.networks)
      ? caps.networks.map(readNetwork).filter((n): n is GatewayNetwork => n !== null)
      : null,
  };
}

/** True when the gateway announced a birth at all (GatewayPro v0.3+). */
export function hasBirth(thing: Pick<Thing, "capabilities" | "capabilities_at"> | null | undefined): boolean {
  return !!thing?.capabilities_at || (!!thing?.capabilities && typeof thing.capabilities === "object");
}

/** True when the gateway accepts config schema v2, so it answers browse and runs non-Modbus drivers. */
export function speaksConfigV2(birth: GatewayBirth): boolean {
  return birth.configVersions.includes(2);
}
